import { useState, useRef } from 'react'
import { FileText, Upload, Inbox, X } from 'lucide-react'
import { Button, ConfirmDialog, DateInput, Select, Toggle } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { DocumentCategory } from '@/api/services/documents.service'
import { useUserDocuments, useDeleteDocument, useUploadDocuments } from '../hooks/useUserDocuments'
import { useArchiveDocument, useUnarchiveDocument } from '@/features/documents/hooks/useDocuments'
import { ArchiveDocumentModal } from '@/features/documents/components/ArchiveDocumentModal'
import { GroupedDocumentList } from '@/features/documents/components/GroupedDocumentList'
import {
  DOCUMENT_TYPE_OPTIONS,
  NO_DOCUMENT_TYPE,
  documentTypeNeedsExpiry,
  missingExpiryMessage,
} from '@/features/documents/utils/documentTypes'
import {
  DOCUMENT_UPLOAD_ACCEPT,
  getDocumentCategoryOptions,
  formatFileSize,
  getDocumentUploadErrorMessage,
  getDocumentUploadHelpText,
  validateDocumentFiles,
} from '../utils/documentUpload'
import { cn } from '@/lib/cn'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const CATEGORY_FILTER_OPTIONS = getDocumentCategoryOptions(true)
const UPLOAD_CATEGORY_OPTIONS = getDocumentCategoryOptions(false)

export interface UserDocumentsSectionProps {
  userId: string
  /** Company id required to enable archive/unarchive actions. */
  companyId?: string | null
  canUpload?: boolean
  canDelete?: boolean
  canArchive?: boolean
  /** @deprecated Use canUpload and canDelete instead */
  readOnly?: boolean
}

export function UserDocumentsSection({
  userId,
  companyId = null,
  canUpload,
  canDelete,
  canArchive = false,
  readOnly = false,
}: UserDocumentsSectionProps): React.ReactElement {
  const effectiveCanUpload = canUpload ?? !readOnly
  const effectiveCanDelete = canDelete ?? !readOnly
  const effectiveCanArchive = canArchive && !!companyId

  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('onboarding-documents')
  const [uploadType, setUploadType] = useState<string>(NO_DOCUMENT_TYPE)
  const [uploadExpiryDate, setUploadExpiryDate] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string | null>(null)
  const [archiveDocId, setArchiveDocId] = useState<string | null>(null)
  const [archiveDocName, setArchiveDocName] = useState<string | undefined>(undefined)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const category: DocumentCategory | undefined =
    categoryFilter && CATEGORY_FILTER_OPTIONS.some((o) => o.value === categoryFilter)
      ? (categoryFilter as DocumentCategory)
      : undefined

  const {
    data: documents = [],
    isLoading,
    error,
    refetch,
  } = useUserDocuments(userId, {
    ...(category ? { category } : {}),
    ...(companyId ? { companyId } : {}),
    includeArchived,
  })
  const deleteDocument = useDeleteDocument(userId)
  const upload = useUploadDocuments(userId)
  const archiveMutation = useArchiveDocument(companyId)
  const unarchiveMutation = useUnarchiveDocument(companyId)

  const handleDeleteClick = (id: string): void => {
    if (!effectiveCanDelete) return
    setDocumentIdToDelete(id)
  }

  const handleConfirmDelete = (): void => {
    if (documentIdToDelete) {
      deleteDocument.mutate(documentIdToDelete)
      setDocumentIdToDelete(null)
    }
  }

  const handleArchiveConfirm = (archiveReason?: string): void => {
    if (!archiveDocId) return
    archiveMutation.mutate(
      { id: archiveDocId, archiveReason },
      {
        onSuccess: () => {
          setArchiveDocId(null)
          refetch()
        },
      },
    )
  }

  const processFiles = (newFiles: FileList | File[]): void => {
    const list = Array.from(newFiles)
    const err = validateDocumentFiles(list)
    if (err) {
      setUploadError(err)
      setSelectedFiles([])
      return
    }
    setUploadError(null)
    setSelectedFiles((prev) => [...prev, ...list])
  }

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (!effectiveCanUpload || !e.dataTransfer.files?.length) return
    processFiles(e.dataTransfer.files)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = e.target.files
    if (!selected?.length) return
    processFiles(selected)
    e.target.value = ''
  }

  const removeFile = (index: number): void => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    setUploadError(null)
  }

  const handleUploadSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setUploadError(null)
    if (selectedFiles.length === 0) {
      setUploadError('Select at least one file')
      return
    }
    const expiryMissing = missingExpiryMessage(uploadType, uploadExpiryDate)
    if (expiryMissing) {
      setUploadError(expiryMissing)
      return
    }
    upload.mutate(
      {
        files: selectedFiles,
        category: uploadCategory,
        documentType: uploadType || undefined,
        expiryDate: uploadExpiryDate
          ? new Date(`${uploadExpiryDate}T23:59:59.999Z`).toISOString()
          : undefined,
      },
      {
        onSuccess: () => {
          setSelectedFiles([])
          setUploadType(NO_DOCUMENT_TYPE)
          setUploadExpiryDate('')
          refetch()
        },
        onError: (err) => {
          setUploadError(getDocumentUploadErrorMessage(err))
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          options={CATEGORY_FILTER_OPTIONS}
          value={categoryFilter}
          onChange={setCategoryFilter}
          placeholder="All categories"
          className="min-w-[160px]"
        />
        {effectiveCanArchive && (
          <Toggle checked={includeArchived} onChange={setIncludeArchived} label="Show archived" />
        )}
      </div>

      {effectiveCanUpload && (
        <form
          onSubmit={handleUploadSubmit}
          className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-600 dark:bg-gray-800/30"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={DOCUMENT_UPLOAD_ACCEPT}
            multiple
            onChange={handleFileSelect}
            className="sr-only"
            aria-label="Choose files"
          />
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <Select
              label="Type"
              options={DOCUMENT_TYPE_OPTIONS}
              value={uploadType}
              onChange={(v) => {
                setUploadType(v)
                setUploadError(null)
              }}
              className="w-full"
            />
            <Select
              label="Category"
              options={UPLOAD_CATEGORY_OPTIONS}
              value={uploadCategory}
              onChange={(v) => setUploadCategory(v as DocumentCategory)}
              placeholder="Category"
              className="w-full"
            />
            <div className="sm:col-span-2">
              <label
                htmlFor={`${userId}-document-expiry`}
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Expiry date{' '}
                <span className="text-gray-400">
                  {documentTypeNeedsExpiry(uploadType) ? '(required)' : '(optional)'}
                </span>
              </label>
              <DateInput
                id={`${userId}-document-expiry`}
                value={uploadExpiryDate}
                onChange={(e) => {
                  setUploadExpiryDate(e.target.value)
                  setUploadError(null)
                }}
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          {uploadError && (
            <p className="mb-2 text-sm text-danger" role="alert">
              {uploadError}
            </p>
          )}
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            {getDocumentUploadHelpText()}
          </p>
          <div className="flex items-stretch gap-2">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
              }}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
              }}
              onDrop={handleDrop}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              aria-label="Drop files or click to browse"
              className={cn(
                'flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors',
                isDragging
                  ? 'border-primary bg-primary/10 dark:bg-primary/20'
                  : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800/50 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer',
              )}
            >
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isDragging ? 'Drop here' : 'Drop files here or click to browse'}
              </span>
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={selectedFiles.length === 0}
              loading={upload.isPending}
              aria-label={upload.isPending ? 'Uploading…' : 'Upload'}
              loadingLabel="Uploading files"
              className="h-11 shrink-0"
            >
              {upload.isPending ? 'Uploading…' : <Upload className="h-4 w-4" aria-hidden />}
            </Button>
          </div>
          {selectedFiles.length > 0 && (
            <ul className="mt-3 space-y-1.5" role="list">
              {selectedFiles.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white py-1.5 pl-2 pr-1.5 dark:border-gray-600 dark:bg-gray-800"
                >
                  <FileText className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                  <span
                    className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100"
                    title={file.name}
                  >
                    {file.name}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    className="shrink-0 p-1 text-gray-500 hover:text-danger dark:text-gray-400 dark:hover:text-danger"
                    onClick={() => removeFile(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </form>
      )}

      {isLoading ? (
        <SectionLoader variant="list" label="Loading documents" />
      ) : error ? (
        <QueryErrorState
          error={error}
          size="inline"
          title="Couldn't load documents"
          onRetry={refetch}
        />
      ) : documents.length === 0 ? (
        <div className="flex min-h-[120px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-8 dark:border-gray-600 dark:bg-gray-800/30">
          <Inbox className="mb-2 h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden />
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            No documents yet. Add files above to upload.
          </p>
        </div>
      ) : (
        <GroupedDocumentList
          documents={documents}
          canArchive={effectiveCanArchive}
          canDelete={effectiveCanDelete}
          isArchivePending={archiveMutation.isPending}
          isUnarchivePending={unarchiveMutation.isPending}
          isDeletePending={deleteDocument.isPending || !!documentIdToDelete}
          onArchive={(document) => {
            setArchiveDocId(document.id)
            setArchiveDocName(document.originalName)
          }}
          onUnarchive={(documentId) => unarchiveMutation.mutate(documentId)}
          onDelete={handleDeleteClick}
        />
      )}

      <ArchiveDocumentModal
        isOpen={archiveDocId !== null}
        onClose={() => setArchiveDocId(null)}
        onConfirm={handleArchiveConfirm}
        isPending={archiveMutation.isPending}
        documentName={archiveDocName}
      />

      <ConfirmDialog
        open={documentIdToDelete !== null}
        onClose={() => setDocumentIdToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete document"
        message="Delete this document?"
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteDocument.isPending}
      />
    </div>
  )
}

UserDocumentsSection.displayName = 'UserDocumentsSection'
